import React, { useState } from 'react'
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Circle as CircleIcon,
} from '@mui/icons-material'
import { ChecklistCategory, ChecklistOption } from '@types'

interface TreeSelectProps {
  category: ChecklistCategory
  value?: any
  onChange?: (value: any) => void
  disabled?: boolean
}

const TreeSelect: React.FC<TreeSelectProps> = ({
  category,
  value,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [expanded, setExpanded] = useState<string[]>([])
  const [selectedValue, setSelectedValue] = useState(value || '')

  const handleToggle = (nodeId: string) => {
    setExpanded(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    )
  }

  const handleSelect = (option: ChecklistOption) => {
    setSelectedValue(option.id)
    onChange?.(option.id)
  }

  const findOptionById = (options: ChecklistOption[], id: string): ChecklistOption | null => {
    for (const option of options) {
      if (option.id.toString() === id) {
        return option
      }
      if (option.children) {
        const found = findOptionById(option.children, id)
        if (found) return found
      }
    }
    return null
  }

  const getSelectedOptionName = () => {
    if (!value) return ''
    const option = findOptionById(category.options || [], value.toString())
    return option?.name || ''
  }

  const renderTreeItems = (options: ChecklistOption[], level: number = 0) => {
    return options.map((option) => (
      <Box key={option.id}>
        <ListItem disablePadding>
          <ListItemButton
            sx={{ 
              pl: level * 2 + 1,
              py: isMobile ? 1 : 0.5,
              minHeight: isMobile ? 48 : 36,
              backgroundColor: selectedValue === option.id ? 'action.selected' : 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
            onClick={() => {
              if (option.children && option.children.length > 0) {
                handleToggle(option.id.toString())
              } else {
                handleSelect(option)
              }
            }}
            disabled={disabled}
          >
            {option.children && option.children.length > 0 ? (
              <ListItemIcon sx={{ minWidth: 32 }}>
                {expanded.includes(option.id.toString()) ? (
                  <ExpandMoreIcon fontSize={isMobile ? "medium" : "small"} />
                ) : (
                  <ChevronRightIcon fontSize={isMobile ? "medium" : "small"} />
                )}
              </ListItemIcon>
            ) : (
              <ListItemIcon sx={{ minWidth: 32 }}>
                <CircleIcon 
                  fontSize="small" 
                  sx={{ 
                    color: selectedValue === option.id ? 'primary.main' : 'action.disabled',
                    fontSize: isMobile ? 12 : 8
                  }} 
                />
              </ListItemIcon>
            )}
            <ListItemText 
              primary={option.name}
              primaryTypographyProps={{
                variant: isMobile ? "body1" : "body2",
                fontWeight: selectedValue === option.id ? 'bold' : 'normal'
              }}
            />
          </ListItemButton>
        </ListItem>
        
        {option.children && option.children.length > 0 && (
          <Collapse in={expanded.includes(option.id.toString())} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {renderTreeItems(option.children, level + 1)}
            </List>
          </Collapse>
        )}
      </Box>
    ))
  }

  // If it's a simple select (no nested structure), use regular Select
  if (!category.options || category.options.every(opt => !opt.children || opt.children.length === 0)) {
    return (
      <FormControl fullWidth disabled={disabled}>
        <InputLabel>{category.name}</InputLabel>
        <Select
          value={value || ''}
          onChange={(e) => onChange?.(e.target.value)}
          label={category.name}
        >
          <MenuItem value="">
            <em>Select an option</em>
          </MenuItem>
          {category.options?.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    )
  }

  // For nested tree structure
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {category.name}
      </Typography>
      
      <Box
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          minHeight: isMobile ? 150 : 200,
          maxHeight: isMobile ? 250 : 300,
          overflow: 'auto',
          backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
        }}
      >
        <List dense={!isMobile} disablePadding>
          {renderTreeItems(category.options || [])}
        </List>
      </Box>

      {value && (
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
          Selected: {getSelectedOptionName()}
        </Typography>
      )}
    </Box>
  )
}

export default TreeSelect
